import React from 'react';
import { Link } from 'react-router-dom';
import SiteNav from './SiteNav';
import {
  Bell,
  Bookmark,
  CalendarDays,
  Clock3,
  Heart,
  Layers,
  Mail,
  Sparkles,
} from 'lucide-react';

const features = [
  {
    icon: Sparkles,
    title: 'One prompt per day',
    description:
      'A focused daily message designed to improve professional habits without adding more noise to your day.',
  },
  {
    icon: Bookmark,
    title: 'Favorites library',
    description:
      'Save the prompts that resonate, then revisit them from a dedicated favorites view.',
  },
  {
    icon: Bell,
    title: 'Local reminders',
    description:
      'Schedule daily reminders and optional follow-up nudges. Notifications stay local to your device.',
  },
  {
    icon: Layers,
    title: 'Category personalization',
    description:
      'Tune your feed toward Trust, Confidence, Focus, Communication, Leadership, Stress, Growth, and Teamwork.',
  },
];

const principles = [
  {
    icon: Heart,
    title: 'Small moments, compounding growth',
    description:
      'Short, daily prompts make personal development sustainable and realistic for busy schedules.',
  },
  {
    icon: Clock3,
    title: 'Low-friction by design',
    description:
      'A clean, lightweight experience that helps you reflect quickly and get back to your day.',
  },
  {
    icon: CalendarDays,
    title: 'Consistency over intensity',
    description:
      'The goal is steady practice over time, supported by reminders and a trackable history.',
  },
];

const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="bb-card p-6">
    <div className="flex items-start gap-4">
      <div className="bb-icon-badge">
        <Icon size={18} strokeWidth={2} />
      </div>
      <div className="min-w-0">
        <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--bb-ink)' }}>
          {title}
        </h3>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--bb-muted)' }}>
          {description}
        </p>
      </div>
    </div>
  </div>
);

const BuildBetter = () => {
  return (
    <div className="buildbetter-page min-h-screen">
      <SiteNav tone="light" />
      <section className="bb-gradient-bg relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 md:px-12 pt-6 pb-12 md:pb-16">
          <div className="flex flex-col items-center text-center">
            <p
              className="bb-animate bb-animate-d1 text-sm uppercase tracking-[0.18em] mb-4"
              style={{ color: 'var(--bb-accent)' }}
            >
              iOS app
            </p>
            <h1
              className="bb-animate bb-animate-d2 text-4xl md:text-6xl font-bold tracking-tight mb-4"
              style={{ color: 'var(--bb-ink)' }}
            >
              Build Better
            </h1>
            <p
              className="bb-animate bb-animate-d3 text-lg md:text-xl max-w-2xl mb-4 leading-relaxed"
              style={{ color: 'var(--bb-muted)' }}
            >
              Turn professional growth into a daily habit.
            </p>

            <p
              className="bb-animate bb-animate-d4 text-sm max-w-xl"
              style={{ color: 'var(--bb-muted)' }}
            >
              One focused prompt a day, personalized to the skills you want to strengthen.
            </p>
            <div className="bb-animate bb-animate-d4 mt-6 flex flex-wrap gap-4 justify-center">
              <a href="mailto:ted.marchildon@gmail.com?subject=Build%20Better" className="bb-btn-primary">
                Ask about Build Better
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="bb-divider" />

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-24">
        <div className="text-center mb-14">
          <span className="bb-section-label mb-4 inline-block">What It Does</span>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: 'var(--bb-ink)' }}>
            Build better habits in a few minutes a day
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {features.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </div>
      </section>

      <div className="bb-divider" />

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-24">
        <div className="text-center mb-14">
          <span className="bb-section-label mb-4 inline-block">Design Principles</span>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: 'var(--bb-ink)' }}>
            Designed for consistency, not intensity
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {principles.map((principle, index) => {
            const Icon = principle.icon;
            return (
              <div key={principle.title} className={`bb-card p-6 bb-animate bb-animate-d${index + 2}`}>
                <div className="bb-icon-badge mb-4">
                  <Icon size={18} strokeWidth={2} />
                </div>
                <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--bb-ink)' }}>
                  {principle.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--bb-muted)' }}>
                  {principle.description}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      <div className="bb-divider" />

      <footer className="bb-footer">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-10">
            <div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--bb-ink)' }}>
                Build Better
              </h3>
              <p className="text-sm mb-4" style={{ color: 'var(--bb-muted)' }}>
                Built by Teddy Marchildon
              </p>
              <a
                href="mailto:ted.marchildon@gmail.com"
                className="bb-link inline-flex items-center gap-2 text-sm"
              >
                <Mail size={14} />
                ted.marchildon@gmail.com
              </a>
            </div>
            <div className="flex flex-col gap-3 text-sm">
              <Link to="/build-better/privacy" className="bb-link">
                Privacy Policy
              </Link>
              <Link to="/" className="bb-link">
                teddymarchildon.com
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BuildBetter;
